import {
  useId,
  type InputHTMLAttributes,
  type ReactNode,
  type SelectHTMLAttributes,
  type TextareaHTMLAttributes,
} from 'react'
import { cn } from '../lib/cn.js'

const CONTROL = cn(
  'w-full rounded-md border border-line bg-raised px-3 text-sm text-ink',
  'placeholder:text-muted disabled:cursor-not-allowed disabled:opacity-60',
  'aria-invalid:border-crimson',
)

/**
 * Поле ввода вместе с подписью, подсказкой и ошибкой.
 *
 * Собрано в один компонент не для краткости, а чтобы связь подписи с полем
 * нельзя было забыть: htmlFor и aria-describedby выводятся из одного
 * сгенерированного идентификатора. Разъехавшись, эта связь ломает и чтение
 * с экрана, и попадание по подписи мышью — а заметить это глазом нельзя.
 */
export interface FieldProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'id'> {
  label: string
  hint?: ReactNode
  error?: string | null
}

export function Field({ label, hint, error, className, ...rest }: FieldProps) {
  const id = useId()
  const hintId = `${id}-hint`
  const errorId = `${id}-error`

  return (
    <div className="flex flex-col gap-1.5">
      <label htmlFor={id} className="text-sm font-medium text-ink-soft">
        {label}
      </label>

      <input
        id={id}
        aria-invalid={error ? true : undefined}
        aria-describedby={cn(hint && hintId, error && errorId) || undefined}
        className={cn(CONTROL, 'h-9', className)}
        {...rest}
      />

      {hint && !error && (
        <p id={hintId} className="text-xs text-muted">
          {hint}
        </p>
      )}

      {error && (
        <p id={errorId} className="text-xs text-crimson">
          {error}
        </p>
      )}
    </div>
  )
}

export interface SelectFieldProps extends Omit<SelectHTMLAttributes<HTMLSelectElement>, 'id'> {
  label: string
  hint?: ReactNode
  error?: string | null
  children: ReactNode
}

export function SelectField({ label, hint, error, className, children, ...rest }: SelectFieldProps) {
  const id = useId()
  const hintId = `${id}-hint`

  return (
    <div className="flex flex-col gap-1.5">
      <label htmlFor={id} className="text-sm font-medium text-ink-soft">
        {label}
      </label>

      <select
        id={id}
        aria-invalid={error ? true : undefined}
        aria-describedby={hint ? hintId : undefined}
        className={cn(CONTROL, 'h-9', className)}
        {...rest}
      >
        {children}
      </select>

      {hint && !error && (
        <p id={hintId} className="text-xs text-muted">
          {hint}
        </p>
      )}

      {error && <p className="text-xs text-crimson">{error}</p>}
    </div>
  )
}

export interface TextareaFieldProps extends Omit<TextareaHTMLAttributes<HTMLTextAreaElement>, 'id'> {
  label: string
  hint?: ReactNode
  error?: string | null
}

export function TextareaField({ label, hint, error, className, ...rest }: TextareaFieldProps) {
  const id = useId()
  const hintId = `${id}-hint`

  return (
    <div className="flex flex-col gap-1.5">
      <label htmlFor={id} className="text-sm font-medium text-ink-soft">
        {label}
      </label>

      <textarea
        id={id}
        aria-invalid={error ? true : undefined}
        aria-describedby={hint ? hintId : undefined}
        className={cn(CONTROL, 'py-2 leading-relaxed', className)}
        {...rest}
      />

      {hint && !error && (
        <p id={hintId} className="text-xs text-muted">
          {hint}
        </p>
      )}

      {error && <p className="text-xs text-crimson">{error}</p>}
    </div>
  )
}

/**
 * Галочка с подписью справа, а не сверху.
 *
 * Отличие от остальных полей намеренное: у галочки подпись читается как
 * продолжение самого переключателя («проверять ответ перед отправкой»),
 * и вынесенная наверх она превращается в заголовок неизвестно чего.
 */
export interface CheckboxFieldProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'id'> {
  label: string
  hint?: ReactNode
}

export function CheckboxField({ label, hint, className, ...rest }: CheckboxFieldProps) {
  const id = useId()
  const hintId = `${id}-hint`

  return (
    <div className="flex flex-col gap-1.5">
      <div className="flex items-start gap-2.5">
        <input
          id={id}
          type="checkbox"
          aria-describedby={hint ? hintId : undefined}
          className={cn(
            'mt-0.5 size-4 shrink-0 rounded border-line text-accent',
            'disabled:cursor-not-allowed disabled:opacity-60',
            className,
          )}
          {...rest}
        />
        <label htmlFor={id} className="text-sm font-medium text-ink-soft">
          {label}
        </label>
      </div>

      {hint && (
        <p id={hintId} className="pl-[26px] text-xs text-muted">
          {hint}
        </p>
      )}
    </div>
  )
}
